/**
 * CodeUnderstandingTab
 * ─────────────────────────────────────────────────────────────────────────────
 * Per-coding-question render of the post-submission Realtime voice follow-up
 * data. Surfaces what the AI asked, how the candidate answered, the AI's
 * verdict per answer, and the overall comprehension score.
 *
 * Data lives in `code_submissions[i].followup_session`. Each row is one
 * coding question. We render one card per row in question_idx order, with
 * 3 distinct visual states:
 *
 *   1. TAKEN — followup_session is a populated object with `questions` array.
 *      Render the full Q&A breakdown + comprehension score + transcript.
 *
 *   2. NOT_ATTEMPTED — was_attempted=false (and therefore followup_session=null
 *      because the backend short-circuits the call). Render a flat "candidate
 *      didn't write code" note so the recruiter knows why there's no Q&A.
 *
 *   3. NOT_RUN / MIC_DENIED — was_attempted=true BUT followup_session=null
 *      (feature flag was off at submission time) OR followup_session exists
 *      but mic_granted=false (candidate refused permission). Render an
 *      amber card with the reason.
 *
 * Hidden entirely when zero coding-question rows exist (e.g. behavioural-
 * only tests). The parent passes `codeSubmissions` straight through.
 */

import React, { useMemo, useState } from 'react';
import {
  MessageSquare, CheckCircle2, AlertTriangle, MicOff, Clock,
  ChevronDown, ChevronUp, AlertCircle, XCircle,
} from 'lucide-react';


// ─── Visual maps ─────────────────────────────────────────────────────────────

// Answer-verdict styling. Keep the keys aligned with
// services/followup/prompt_builder.py:ANSWER_VERDICTS.
const ANSWER_VERDICT_META = {
  correct:   { label: 'Correct',   icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial:   { label: 'Partial',   icon: AlertCircle,  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  wrong:     { label: 'Wrong',     icon: XCircle,      cls: 'bg-red-50 text-red-700 border-red-200' },
  evasive:   { label: 'Evasive',   icon: AlertTriangle,cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  no_answer: { label: 'No answer', icon: XCircle,      cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

// Session-level overall verdict styling. Same mapping rationale.
const SESSION_VERDICT_META = {
  passed:    { label: 'Passed',    cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  concerned: { label: 'Concerned', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  failed:    { label: 'Failed',    cls: 'bg-red-100 text-red-700 border-red-300' },
};

const QUESTION_TYPE_LABEL = {
  walkthrough:  'Walkthrough',
  prediction:   'Prediction',
  modification: 'Modification',
};


// ─── Helpers ─────────────────────────────────────────────────────────────────

const _formatDuration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const _formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.valueOf())) return '';
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

// Comprehension score → colour band for the progress bar. Same buckets as
// the AI's own band guidance in prompt_builder.py:
//   90+ = ownership · 60-89 mostly · 30-59 gaps · <30 cannot explain
const _scoreColour = (score) => {
  const s = Number(score) || 0;
  if (s >= 90) return 'bg-emerald-500';
  if (s >= 60) return 'bg-amber-500';
  if (s >= 30) return 'bg-orange-500';
  return 'bg-red-500';
};


// ─── Mic-denial reason → human label ─────────────────────────────────────────

const MIC_DENIAL_LABEL = {
  user_denied:         'Candidate denied microphone access',
  no_device:           'No microphone detected on the device',
  device_busy:         'Microphone in use by another application',
  browser_unsupported: 'Browser blocked microphone access',
};


// ─── Component ───────────────────────────────────────────────────────────────

export default function CodeUnderstandingTab({ codeSubmissions }) {
  const submissions = useMemo(() => {
    if (!Array.isArray(codeSubmissions)) return [];
    // Sort by question_idx so cards render in test order regardless of
    // what order the DB returned them (we already sort in the SQL but
    // belt-and-suspenders for any future caller).
    return [...codeSubmissions]
      .filter((s) => s && typeof s.question_idx !== 'undefined')
      .sort((a, b) => (a.question_idx ?? 0) - (b.question_idx ?? 0));
  }, [codeSubmissions]);

  // Roll-up metric: how many coding questions did the candidate actually
  // "explain" well? We define "passed" at the AI's overall_verdict level
  // because that already accounts for the per-answer verdicts.
  const rollup = useMemo(() => {
    let total = 0;
    let taken = 0;
    let passed = 0;
    let concerned = 0;
    let failed = 0;
    let notAttempted = 0;
    let micDenied = 0;
    let scoreSum = 0;
    let scoreN = 0;
    for (const s of submissions) {
      total += 1;
      if (s.was_attempted === false) {
        notAttempted += 1;
        continue;
      }
      const fs = s.followup_session;
      if (!fs) continue;        // feature was off OR call failed silently
      if (fs.mic_granted === false) {
        micDenied += 1;
        continue;
      }
      taken += 1;
      const v = fs.overall_verdict;
      if (v === 'passed')    passed    += 1;
      if (v === 'concerned') concerned += 1;
      if (v === 'failed')    failed    += 1;
      if (typeof fs.comprehension_score === 'number') {
        scoreSum += fs.comprehension_score;
        scoreN   += 1;
      }
    }
    const avgScore = scoreN ? Math.round(scoreSum / scoreN) : null;
    return { total, taken, passed, concerned, failed, notAttempted, micDenied, avgScore };
  }, [submissions]);


  // Edge case: parent hasn't loaded data yet — return a quiet skeleton.
  if (!Array.isArray(codeSubmissions)) {
    return (
      <div className="text-sm text-slate-500 p-6 text-center">
        Loading code understanding data…
      </div>
    );
  }

  // Edge case: this test has no coding questions at all. Hide the tab body
  // rather than render an empty header — recruiters get confused otherwise.
  if (submissions.length === 0) {
    return (
      <div className="text-sm text-slate-500 p-6 text-center bg-slate-50 rounded-md border border-slate-200">
        No coding submissions recorded for this test.
      </div>
    );
  }


  return (
    <div className="space-y-4">

      {/* Roll-up header — gives the recruiter the gist in 2 seconds */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5 text-emerald-600" />
          <h3 className="text-base font-semibold text-slate-900">Code Comprehension Verification</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <RollupTile label="Coding questions"   value={rollup.total} />
          <RollupTile label="Follow-up taken"    value={rollup.taken}   accent={rollup.taken ? 'emerald' : null} />
          <RollupTile label="Skipped (not attempted)" value={rollup.notAttempted} accent={rollup.notAttempted ? 'slate' : null} />
          <RollupTile label="Mic denied"         value={rollup.micDenied} accent={rollup.micDenied ? 'red' : null} />
          <RollupTile label="Avg comprehension"  value={rollup.avgScore === null ? '—' : `${rollup.avgScore}/100`} />
        </div>
      </div>

      {/* Per-question cards */}
      {submissions.map((sub) => (
        <QuestionCard key={sub.question_idx} sub={sub} />
      ))}

    </div>
  );
}


// ─── Roll-up tile ────────────────────────────────────────────────────────────

function RollupTile({ label, value, accent = null }) {
  const accentCls = (
    accent === 'emerald' ? 'text-emerald-700' :
    accent === 'red'     ? 'text-red-700' :
    accent === 'slate'   ? 'text-slate-700' :
                           'text-slate-900'
  );
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${accentCls}`}>{value}</div>
    </div>
  );
}


// ─── Per-question card ──────────────────────────────────────────────────────

function QuestionCard({ sub }) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const fs = sub.followup_session;

  // ─── State 1: NOT_ATTEMPTED ────────────────────────────────────────────────
  if (sub.was_attempted === false) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Question {sub.question_idx + 1}
            </span>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-xs text-slate-600">{sub.language || '—'}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
            Not attempted
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Candidate submitted boilerplate without writing code. No
          verification call ran.
        </p>
      </div>
    );
  }

  // ─── State 2: NOT_RUN ─────────────────────────────────────────────────────
  // was_attempted=true but no session — feature flag was off at submit time,
  // OR the call failed BEFORE persisting anything to the DB.
  if (!fs) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Question {sub.question_idx + 1}
            </span>
            <span className="text-xs text-slate-500">·</span>
            <span className="text-xs text-slate-600">{sub.language || '—'}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
            No follow-up call
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          The follow-up-calls feature was disabled when this question was
          submitted, or the call could not start. Code was submitted
          normally.
        </p>
      </div>
    );
  }

  // ─── State 3: MIC_DENIED ──────────────────────────────────────────────────
  if (fs.mic_granted === false) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-bold uppercase tracking-wide text-amber-700">
              Question {sub.question_idx + 1}
            </span>
            <span className="text-xs text-amber-600">·</span>
            <span className="text-xs text-amber-700">{sub.language || '—'}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
            <MicOff className="w-3 h-3" />
            Mic denied
          </span>
        </div>
        <div className="mt-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <p className="font-semibold">
              Candidate could not complete the verification call.
            </p>
            <p className="mt-1">
              {MIC_DENIAL_LABEL[fs.mic_denied_reason] || 'Microphone access could not be granted.'}
            </p>
            <p className="mt-1 italic">
              Recruiter signal: refusing the voice check on a submission with
              real code is a strong indicator the candidate could not explain
              what they wrote.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── State 4: TAKEN — the rich render path ────────────────────────────────
  const verdictMeta = SESSION_VERDICT_META[fs.overall_verdict] || SESSION_VERDICT_META.concerned;
  const transcriptLines = Array.isArray(fs.transcript_inline) ? fs.transcript_inline : [];

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">

      {/* Card header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Question {sub.question_idx + 1}
          </span>
          <span className="text-xs text-slate-500">·</span>
          <span className="text-xs text-slate-600">{sub.language || '—'}</span>
          {typeof fs.duration_s === 'number' && (
            <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-slate-500">
              <Clock className="w-3 h-3" />
              {_formatDuration(fs.duration_s)}
            </span>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border ${verdictMeta.cls}`}>
          {verdictMeta.label}
        </span>
      </div>

      {/* Comprehension score with bar */}
      {typeof fs.comprehension_score === 'number' && (
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
              Comprehension score
            </span>
            <span className="text-lg font-bold text-slate-900 tabular-nums">
              {fs.comprehension_score}
              <span className="text-sm font-medium text-slate-400">/100</span>
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${_scoreColour(fs.comprehension_score)} transition-[width] duration-300`}
              style={{ width: `${Math.max(0, Math.min(100, fs.comprehension_score))}%` }}
            />
          </div>
        </div>
      )}

      {/* Per-question Q&A list */}
      {Array.isArray(fs.questions) && fs.questions.length > 0 && (
        <div className="space-y-2">
          {fs.questions.map((q, idx) => {
            const meta = ANSWER_VERDICT_META[q.verdict] || ANSWER_VERDICT_META.no_answer;
            const Icon = meta.icon;
            return (
              <div key={idx} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        Q{q.idx ?? idx + 1}
                      </span>
                      <span className="text-[10px] text-slate-400">·</span>
                      <span className="text-[10px] font-semibold text-slate-600">
                        {QUESTION_TYPE_LABEL[q.type] || q.type || 'Question'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-800 mt-1 leading-snug">
                      {q.text || <span className="text-slate-400 italic">(no question text)</span>}
                    </p>
                  </div>
                  <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${meta.cls}`}>
                    <Icon className="w-3 h-3" />
                    {meta.label}
                  </span>
                </div>
                {q.evidence && (
                  <p className="text-[11px] text-slate-600 mt-2 italic leading-snug border-t border-slate-200 pt-2">
                    AI's note: {q.evidence}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty-questions fallback (rare — happens when end_session fired
          before any record_question / record_answer). */}
      {(!Array.isArray(fs.questions) || fs.questions.length === 0) && (
        <p className="text-xs text-slate-500 italic">
          The session ended before any questions were recorded
          (likely network or timeout). Verdict was derived from the
          partial data above.
        </p>
      )}

      {/* Full transcript expansion — handy when the recruiter wants to
          read what the candidate actually said verbatim. Only shows when
          we have transcript lines to render. */}
      {transcriptLines.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setTranscriptOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline"
            aria-expanded={transcriptOpen}
          >
            {transcriptOpen ? (
              <><ChevronUp className="w-3.5 h-3.5" /> Hide transcript</>
            ) : (
              <><ChevronDown className="w-3.5 h-3.5" /> Show full transcript ({transcriptLines.length} lines)</>
            )}
          </button>
          {transcriptOpen && (
            <div className="mt-2 bg-slate-50 border border-slate-200 rounded-md p-3 max-h-64 overflow-y-auto space-y-1.5 text-xs">
              {transcriptLines.map((line, idx) => (
                <div key={idx}>
                  <span className={`text-[10px] uppercase tracking-wide font-semibold mr-2 ${
                    line.role === 'assistant' ? 'text-emerald-600' : 'text-blue-600'
                  }`}>
                    {line.role === 'assistant' ? 'AI' : 'You'}
                  </span>
                  <span className="text-slate-700">{line.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer — when was this captured */}
      {fs.ended_at && (
        <p className="text-[10px] text-slate-400 mt-3 text-right">
          Recorded {_formatTimestamp(fs.ended_at)}
        </p>
      )}

    </div>
  );
}
